import { useMutation } from "react-query";
import styled from "styled-components";
import externalAxios from "axios";
import { useTranslation } from "react-i18next";
import { axios } from "../lib/request";
import { Button, ButtonRow } from "../App.styles";
import { Tooltip } from "@material-ui/core";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTimes } from "@fortawesome/free-solid-svg-icons";
import VideoThumbnail from "./VideoThumbnail";
import useVideoThumbnail from "./VideoThumbnail/useVideoThumbnail";
import { acceptedVideos } from '../consts/accepted-files'
import { useRef } from "react";

const ImageUploadWrapper = styled.div`
  display: flex;
  flex-direction: column;
  flex-wrap: wrap;

  .form-label {
    font-size: 0.8rem;
  }

  ${ButtonRow} {
    margin-top: 0.75rem;
    margin-bottom: 0.25rem;

    ${Button} {
      margin-right: 0.25rem;
      max-width: 200px;
      text-overflow: ellipsis;
    }
  }

  .video-thumbnail__processing {
    font-size: 11px;
  }
`;

const FileWrapper = styled.div`
  margin-right: 0.5rem;

  input[type="file"] {
    display: none;
  }

  .form-control {
    font-size: 0.8rem;
  }
`;

const ActionWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;

  button {
    margin-bottom: 0.3125rem;
    padding: .625rem 1rem;
  }
`;

const UploadBtn = styled.label(({ theme }) => `
    display: inline-block;
    border-radius: ${theme.rounded.sm};

    &:focus {
        .btn {
            outline: 2px solid ${theme.accent2Color};
        }
    }
`);

const translateKey = (key) => {
  switch (key) {
    case "ya":
      return "correct";
    case "naw":
      return "incorrect";
    case "arrival_video":
      return "arrival";
    case "overlay":
      return "sticker";
    default:
      return key;
  }
};

const NewVideoUpload = ({ label, name, useQuery, width = 270 }) => {
  const { t } = useTranslation();
  const [{ assets, placeholders, id }, setCollection] = useQuery();
  const key = translateKey(name);
  const hookProps = useVideoThumbnail({ key, useQuery })
  const {
    loadedState,
    thumb,
  } = hookProps
  const [, setLoaded] = loadedState;
  const inputFileRef = useRef(null);
  const handleFileLabel = (e) => {
      if (e.key !== "Enter" || inputFileRef.current === null) return;
      inputFileRef.current.click();
  };
  const { isLoading, mutateAsync } = useMutation(async (file) => {
    const [ext] = file.name.split(".").reverse();

    const url = `${
      process.env.REACT_APP_API_SIGN_URL
    }/${ext.toLocaleLowerCase()}`;
    const response = await axios.get(url);
    const { data } = response || {}
    const options = { headers: { "Content-Type": file.type } };

    await externalAxios.put(data.signature, file, options);

    const [uuid, extension] = data.name.split(".");
    const videoIndex = Math.max(0, assets.videos.findIndex(v => v.name === key))
    const videos = [...assets.videos || []]
    videos.splice(videoIndex, 1, {
      name: key,
      urls: {
        mp4: `https://${process.env.REACT_APP_WHITELABEL}-assets.socialscavenger.com/videos/${uuid}.mp4`,
        png: `https://${process.env.REACT_APP_WHITELABEL}-assets.socialscavenger.com/images/${uuid}-00001.png`
      }
    })
    return await setCollection({
      placeholders: [...(placeholders || []), { key, uuid }],
      [`${name}_media_uuid`]: uuid,
      [`${name}_media_extension`]: extension.toLocaleLowerCase(),
      assets: {
        ...assets,
        videos,
      }
    });
  });

  const upload = async (file) => {
    if (file) {
      setLoaded(false);
      await mutateAsync(file);
    }
  };

  const clear = () => {
    const media = assets.videos.filter((asset) => asset.name !== key);

    setCollection({
      assets: {
        ...assets,
        videos: [...media, { name: key, urls: {} }],
      },
      [`${name}_media_uuid`]: null,
      [`${name}_media_extension`]: null,
    });

    setLoaded(false);
  };

  const fileId = `${name}-video-${id}-upload`

  return (
    <ImageUploadWrapper className="form-outline">
        {label && <label className="form-label">{label}</label>}
        <VideoThumbnail label={label} width={width} isLoading={isLoading} {...hookProps} />
        <ActionWrapper>
          <UploadBtn htmlFor={fileId} tabIndex={0} onKeyUp={handleFileLabel}>
            <FileWrapper>
              <input
                accept={acceptedVideos.join(',')}
                className="form-control"
                id={fileId}
                type="file"
                onChange={({ target }) => upload(target.files[0])}
                ref={inputFileRef}
              />
            </FileWrapper>
            <Button tag="span" color="warning" className="btn--flat">
              {t('Select File')}
            </Button>
          </UploadBtn>
          {
            thumb
              ? (
                <Tooltip title={t(`Delete video`)}>
                  <Button onClick={clear} color="danger" className="btn--flat">
                    <FontAwesomeIcon icon={faTimes} />
                  </Button>
                </Tooltip>
              )
              : null
          }
        </ActionWrapper>
    </ImageUploadWrapper>
  );
};

export default NewVideoUpload;
